import config from '../config';
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "../style/deviceDetails.css";
import { Briefcase, Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import Button from "../components/Button";

const DeviceDetail = () => {
  const navigate = useNavigate();
  const { authData } = useAuth();
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");
  const [savedCampaignIds, setSavedCampaignIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { device, orgId } = location.state || {
    device: {
      timezone: "America/New_York",
      appVersion: "1.15",
      buildNumber: "43",
      organizationName: "BackpackEMR Trial",
      OrganizationId: null,
    },
  };

  const { organizationId, deviceId } = useParams();
  const [organizationName, setOrganizationName] = useState(
    device?.organizationName || ""
  );

  const [deviceCampaigns, setDeviceCampaigns] = useState([]);
  const [deviceCampaignSearch, setDeviceCampaignSearch] = useState("");

  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [availableCampaignSearch, setAvailableCampaignSearch] = useState("");
  const [allCampaigns, setAllCampaigns] = useState([]);

  // Fetch organization name if not provided
  useEffect(() => {
    if (!organizationName && organizationId) {
      axios
        .get(
          `${config.BASE_URL}/organizations/${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          setOrganizationName(response.data.name);
        })
        .catch((error) => {
          console.error("Error fetching organization name:", error);
          setOrganizationName("Unknown Organization");
        });
    }
  }, [organizationId, authData, organizationName]);

  // Initial data loading - get all campaigns and device campaigns
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        // Fetch all organization campaigns
        const allCampaignsResponse = await axios.get(
          `${config.BASE_URL}/campaigns/organization/${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        const fetchedCampaigns = allCampaignsResponse.data || [];
        setAllCampaigns(fetchedCampaigns);
        
        // Only fetch device campaigns if we have a valid device ID
        if (device.id) {
          const deviceCampaignResponse = await axios.get(
            `${config.BASE_URL}/devices/campaigns/${device.id}`,
            {
              headers: {
                Authorization: `Bearer ${authData.accessToken}`,
                "Content-Type": "application/json",
              },
            }
          );

          const campaignIds = deviceCampaignResponse.data.map(
            (item) => item.CampaignId
          );

          // If we have associated campaign IDs
          if (campaignIds && Array.isArray(campaignIds) && campaignIds.length > 0) {
            // Find the device's campaigns from the full list
            const deviceCampaigns = fetchedCampaigns.filter(campaign => 
              campaignIds.includes(campaign.id)
            );
            
            setDeviceCampaigns(deviceCampaigns);
            setSavedCampaignIds(deviceCampaigns.map(c => c.id));
            
            // Set available campaigns as those not associated with the device
            setAvailableCampaigns(
              fetchedCampaigns.filter(
                campaign => !campaignIds.includes(campaign.id)
              )
            );
          } else {
            // No campaigns associated with device
            setDeviceCampaigns([]);
            setAvailableCampaigns(fetchedCampaigns);
          }
        } else {
          // No device ID, so all campaigns are available
          setAvailableCampaigns(fetchedCampaigns);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [organizationId, device.id, authData]);

  // Handle adding a campaign to the device (local state only, no API call)
  const handleAddCampaign = (campaign) => {
    setDeviceCampaigns(prev => [...prev, campaign]);
    setAvailableCampaigns(prev => prev.filter(c => c.id !== campaign.id));
  };

  // Handle removing a campaign from the device (local state only, no API call)
  const handleRemoveCampaign = (campaign) => {
    setAvailableCampaigns(prev => [...prev, campaign]);
    setDeviceCampaigns(prev => prev.filter(c => c.id !== campaign.id));
  };

  // Filter campaigns based on search term
  const filterCampaigns = (campaigns, searchTerm) => {
    if (!searchTerm) return campaigns;
    
    return campaigns.filter(
      (campaign) =>
        campaign.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.focus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.Account?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.Location?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Save changes (only API call when saving)
  const handleSave = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
  
    const payload = {
      DeviceId: device.id,
      CampaignId: deviceCampaigns.map(campaign => campaign.id),
    };
  
    const method = "PUT";
    const url = `${config.BASE_URL}/devices/${device.id}/campaigns`;
  
    axios({
      method,
      url,
      data: payload,
      headers: {
        Authorization: `Bearer ${authData.accessToken}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        console.log("Successfully saved device campaigns:", response.data);
        setPopupMessage("Device updated successfully!");
        setPopupType("success");
        setShowPopup(true);
        setSavedCampaignIds(deviceCampaigns.map(c => c.id));
        
        setTimeout(() => {
          setShowPopup(false);
          setIsSubmitting(false);
          navigate(`/organization/${organizationId}/devices`, {
            state: { orgId: organizationId },
          });
        }, 4000);
      })
      .catch((error) => {
        console.error("Error saving device campaigns:", error);
        setPopupMessage("Error saving device!");
        setPopupType("error");
        setShowPopup(true);
        
        setTimeout(() => {
          setShowPopup(false);
          setIsSubmitting(false); 
        }, 4000);
      });
  };
  
  // Handle unassociating device from organization
  const handleUnassociate = async () => {
    if (!device || !device.id) {
      console.error("Invalid device details");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await axios.put(
        `${config.BASE_URL}/devices/${device.id}`,
        { OrganizationId: null },
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setPopupMessage("Device removed successfully");
        setPopupType("success");
        setShowPopup(true);
        
        setTimeout(() => {
          setShowPopup(false);
          navigate(`/organization/${organizationId}/devices`, {
            state: { orgId: organizationId },
          });
        }, 2000);
      }
    } catch (error) {
      console.error("Error removing device from organization:", error);
      setPopupMessage("Error removing device");
      setPopupType("error");
      setShowPopup(true);
      
      setTimeout(() => {
        setShowPopup(false);
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if form has unsaved changes
  const hasFormChanges = () => {
    const currentIds = deviceCampaigns.map(c => c.id).sort();
    const savedIds = [...savedCampaignIds].sort();
    return JSON.stringify(currentIds) !== JSON.stringify(savedIds);
  };
  
  // Handle back button click
  const handleBackClick = () => {
    navigate(`/organization/${organizationId}/devices`, {
      state: { orgId: organizationId },
    });
  };

  // Sort campaigns by name for better usability
  const sortedDeviceCampaigns = [...deviceCampaigns].sort((a, b) => 
    a.name?.localeCompare(b.name || '')
  );
  
  const sortedAvailableCampaigns = [...availableCampaigns].sort((a, b) => 
    a.name?.localeCompare(b.name || '')
  );

  return (
    <div className="device-detail-container">
      {showPopup && (
        <div className={`popup-${popupType}`}>
          {popupMessage}
        </div>
      )}

      <div className="device-detail-content-wrapper">
        <div className="header-deviceDetail">
          <Button
            className="back-button-device-details"
            onClick={handleBackClick}
            text="← Back"
            color="#006d7d"
          />
        </div>

        {/* Device info section - unchanged */}
        <div className="device-info-grid">
          <div className="info-row">
            <div className="info-label">
              <strong>Brand</strong>
            </div>
            <div className="info-value">{device.brand || "-"}</div>
          </div>
          <div className="info-row">
            <div className="info-label">
              <strong>Model</strong>
            </div>
            <div className="info-value">{device.device?.model || "-"}</div>
          </div>
          <div className="info-row">
            <div className="info-label">
              <strong>Device Id</strong>
            </div>
            <div className="info-value">{device.id || "-"}</div>
          </div>
          <div className="info-row">
            <div className="info-label">
              <strong>Timezone</strong>
            </div>
            <div className="info-value">{device.device?.timezone || "-"}</div>
          </div>
          <div className="info-row">
            <div className="info-label">
              <strong>App Version</strong>
            </div>
            <div className="info-value">{device.device?.appVersion || "-"}</div>
          </div>
          <div className="info-row">
            <div className="info-label">
              <strong>Bundle Id</strong>
            </div>
            <div className="info-value">{device.device?.bundleId || "-"}</div>
          </div>
          <div className="info-row">
            <div className="info-label">
              <strong>Build Number</strong>
            </div>
            <div className="info-value">{device.device?.buildNumber || "-"}</div>
          </div>
        </div>

        {/* Campaigns section with updated loader */}
        <div className="campaigns-container">
          <div className="card">
            <div className="card-content">
              {isLoading ? (
                <div className="campaign-loading-container">
                  <Loader2 className="campaign-spinner" size={32} />
                  <p>Loading campaign data...</p>
                </div>
              ) : (
                <div className="campaigns-grid">
                  <div className="campaign-section">
                    <h5>Device Campaigns</h5>
                    <div className="search-box">
                      <input
                        type="text"
                        placeholder="Search for..."
                        value={deviceCampaignSearch}
                        onChange={(e) => setDeviceCampaignSearch(e.target.value)}
                      />
                      <Button
                        className="device-detail-clear-button"
                        onClick={() => setDeviceCampaignSearch("")}
                        text="Clear"
                        color={deviceCampaignSearch ? "#006d7d" : "grey"}
                        disabled={!deviceCampaignSearch}
                      />
                    </div>
                    <div className="campaign-list">
                      {filterCampaigns(sortedDeviceCampaigns, deviceCampaignSearch).map(
                        (campaign) => (
                          <div
                            key={campaign.id}
                            className="campaign-item"
                            onClick={() => handleRemoveCampaign(campaign)}
                          >
                            <Briefcase className="campaign-icon large-icon" />
                            <div className="campaign-details">
                              <div className="campaign-name">{campaign.name}</div>
                              <div className="campaign-description">
                                {campaign.Account?.name ? `${campaign.Account.name} : ` : ""}
                                {campaign.Location?.name || ""}
                              </div>
                            </div>
                          </div>
                        )
                      )}
                      {filterCampaigns(sortedDeviceCampaigns, deviceCampaignSearch).length === 0 && (
                        <div className="no-campaigns">
                          {deviceCampaigns.length === 0 ? "No campaigns assigned to this device" : "No matching campaigns found"}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="campaign-section">
                    <h5>{organizationName || "Organization"} Campaigns</h5>
                    <div className="search-box">
                      <input
                        type="text"
                        placeholder="Search for..."
                        value={availableCampaignSearch}
                        onChange={(e) => setAvailableCampaignSearch(e.target.value)}
                      />
                      <Button
                        className="device-detail-clear-button"
                        onClick={() => setAvailableCampaignSearch("")}
                        text="Clear"
                        color={availableCampaignSearch ? "#006d7d" : "grey"}
                        disabled={!availableCampaignSearch}
                      />
                    </div>
                    <div className="campaign-list">
                      {filterCampaigns(sortedAvailableCampaigns, availableCampaignSearch).map(
                        (campaign) => (
                          <div
                            key={campaign.id}
                            className="campaign-item"
                            onClick={() => handleAddCampaign(campaign)}
                          >
                            <Briefcase className="campaign-icon large-icon" />
                            <div className="campaign-details">
                              <div className="campaign-name">{campaign.name}</div>
                              <div className="campaign-description">
                                {campaign.Account?.name ? `${campaign.Account.name} : ` : ""}
                                {campaign.Location?.name || ""}
                              </div>
                            </div>
                          </div>
                        )
                      )}
                      {filterCampaigns(sortedAvailableCampaigns, availableCampaignSearch).length === 0 && (
                        <div className="no-campaigns">
                          {availableCampaigns.length === 0 ? "No other campaigns available" : "No matching campaigns found"}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          className="save-button"
          onClick={handleSave}
          disabled={isSubmitting || !hasFormChanges()}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </button>

        <div className="remove-section">
          <div className="card">
            <div className="card-content">
              <div className="remove-container">
                <div className="remove-text">
                  Remove device from organization "{organizationName || "Loading..."}"
                </div>
                <div className="remove-button-container">
                  <button 
                    className="remove-button" 
                    onClick={handleUnassociate}
                    disabled={isSubmitting}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceDetail;